/**
 * iter93.2 — Predicted-spend pill for the dashboard header.
 * iter93.3 — Adds a quiet, dismissible transition toast that fires ONCE
 * per session when a user crosses green→amber or amber/green→red.
 *
 * Fetches GET /api/users/me/credit-projection once on mount and every 60s,
 * shows a color-coded compact pill ("XX% projected"), and reveals the full
 * breakdown in a tooltip on hover/focus.
 *
 * Design notes:
 *  - Hidden entirely for admin / unlimited-cap users (no noise).
 *  - Hidden on mobile viewports — space is tight next to the bell + avatar.
 *  - Click routes to /dashboard/subscription so users on the red/amber
 *    track can act immediately.
 *  - Transition toast persists the last-notified level in `sessionStorage`
 *    so it fires ONCE per browser session. Falling back to green clears
 *    the flag so a subsequent climb re-triggers the alert.
 */
import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const REFRESH_MS = 60_000;
const ALERT_SESSION_KEY = 'dotbox:credit-alert-shown-level';

const COLOR_BY_LEVEL = {
  green: 'bg-emerald-950/50 text-emerald-300 border-emerald-800/50 hover:bg-emerald-900/60',
  amber: 'bg-amber-950/50 text-amber-300 border-amber-800/50 hover:bg-amber-900/60',
  red: 'bg-red-950/60 text-red-300 border-red-800/50 hover:bg-red-900/70',
};

const CreditProjectionPill = () => {
  const navigate = useNavigate();
  const [projection, setProjection] = useState(null);
  const [loading, setLoading] = useState(true);

  // Poll the projection endpoint every 60s and store the latest snapshot.
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const r = await api.get('/users/me/credit-projection');
        if (mounted) setProjection(r.data);
      } catch (e) {
        // Best-effort — pill quietly hides if the endpoint fails.
        // No toast; the user's flow is unaffected.
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    const iv = setInterval(load, REFRESH_MS);
    return () => {
      mounted = false;
      clearInterval(iv);
    };
  }, []);

  // iter93.3 — Transition-triggered toast. Fires once per session on:
  //   green → amber   (approaching cap)
  //   green → red     (over projection)
  //   amber → red     (escalation)
  // Down-transitions (any → green) reset the session flag so a
  // subsequent climb re-triggers the alert.
  useEffect(() => {
    if (!projection || projection.is_admin || projection.unlimited) return;
    const current = projection.alert_level;
    if (!current) return;

    let stored = 'green';
    try {
      stored = window.sessionStorage.getItem(ALERT_SESSION_KEY) || 'green';
    } catch {
      // sessionStorage disabled (e.g. private mode). Fall through — toast
      // may re-fire on refresh but that's acceptable for a non-noisy alert.
    }

    if (current === 'green') {
      try { window.sessionStorage.removeItem(ALERT_SESSION_KEY); } catch { /* noop */ }
      return;
    }

    const shouldFire =
      (current === 'amber' && stored === 'green') ||
      (current === 'red' && (stored === 'green' || stored === 'amber'));

    if (!shouldFire) return;

    const isRed = current === 'red';
    const title = isRed
      ? "You're on pace to exceed your monthly AI credit cap"
      : "You're approaching your monthly AI credit cap";
    const description =
      `Projected ${projection.over_projection_pct}% of ${projection.cap} credits by month end ` +
      `(${projection.projected_end_of_month} / ${projection.cap}). ` +
      `${projection.days_remaining_in_month} day${projection.days_remaining_in_month === 1 ? '' : 's'} remaining.`;

    const toastFn = isRed ? toast.error : toast.warning;
    toastFn(title, {
      description,
      duration: 12000,
      id: `credit-alert-${current}`, // sonner dedupe if fired twice in a tick
      action: {
        label: 'Upgrade',
        onClick: () => navigate('/dashboard/subscription'),
      },
      // Provides a stable hook for QA / e2e assertions.
      className: `data-[dotbox-alert=${current}]`,
    });

    try {
      window.sessionStorage.setItem(ALERT_SESSION_KEY, current);
    } catch { /* noop */ }
  }, [projection, navigate]);

  if (loading || !projection) return null;
  // Hide for admin / unlimited to avoid distraction
  if (projection.is_admin || projection.unlimited) return null;

  const {
    used,
    cap,
    trailing_7d_used,
    daily_rate,
    projected_end_of_month,
    days_remaining_in_month,
    over_projection_pct,
    alert_level,
  } = projection;

  const colorClass = COLOR_BY_LEVEL[alert_level] || COLOR_BY_LEVEL.green;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => navigate('/dashboard/subscription')}
            className={`hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition ${colorClass}`}
            data-testid="credit-projection-pill"
            data-alert-level={alert_level}
            aria-label={`Credit usage projection: ${over_projection_pct}% of monthly cap`}
          >
            <Sparkles className="h-3 w-3" />
            <span>{over_projection_pct}% projected</span>
          </button>
        </TooltipTrigger>
        <TooltipContent
          className="max-w-xs"
          data-testid="credit-projection-tooltip"
        >
          <div className="space-y-1 text-xs">
            <p className="font-semibold text-slate-100 mb-1">
              Predicted monthly spend
            </p>
            <p className="text-slate-300">
              Used:{' '}
              <strong className="text-slate-100">
                {used} / {cap}
              </strong>{' '}
              credits
            </p>
            <p className="text-slate-300">
              Trailing 7 days:{' '}
              <strong className="text-slate-100">{trailing_7d_used}</strong>{' '}
              credits ({daily_rate}/day)
            </p>
            <p className="text-slate-300">
              Projected end-of-month:{' '}
              <strong className="text-slate-100">
                {projected_end_of_month}
              </strong>{' '}
              credits
            </p>
            <p className="text-slate-400">
              {days_remaining_in_month} day
              {days_remaining_in_month === 1 ? '' : 's'} remaining
            </p>
            {alert_level !== 'green' && (
              <p
                className={`pt-1.5 border-t border-[#1F2A4D] mt-1.5 ${
                  alert_level === 'red' ? 'text-red-300' : 'text-amber-300'
                }`}
              >
                {alert_level === 'red'
                  ? 'On pace to exceed your cap — click to upgrade or add credits.'
                  : 'Approaching your cap — consider throttling or upgrading.'}
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default CreditProjectionPill;
